import clsx from 'clsx';
import FetchFailureIcon from '../_custom/FetchFailureIcon/FetchFailureIcon';
import SourceFileIcon from './SourceFileIcon';
import SourceFileOpenIcon from './SourceFileOpenIcon';
import { useProvisionalSourceFileTagLogic } from './useProvisionalSourceFileTagLogic';
import { NOT_AVAILABLE_LABEL, SOURCE_UNAVAILABLE_LABEL, REFERENCE_DOCUMENTS_ROW_LABEL, REFERENCE_DOC_TEST_IDS } from './constants';
import { ProvisionalSourceFileTagProps, SourceFileTagDisplayState } from './types';
import styles from './ProvisionalSourceFileTag.module.scss';

/**
 * Renders ONE cell of the "Reference Documents" row that `AuditedStatementTable` mounts per
 * period/FY column (see that file's `referenceDocumentsRow` prop). Four states, each visually
 * distinct so a resolved-empty period is never confused with a failed lookup:
 *   - nothing (no `status` at all — not a tagged surface, e.g. Audited/otherMis/the PDF report)
 *   - "Not Available" with a muted icon (a resolved period with zero files)
 *   - the first file shown inline (truncated; itself a real, focusable control that opens it),
 *     plus a "+ N more" disclosure once there is more than one file — hover, click, or Enter/Space
 *     reveals a popover listing the rest (Tab reaches each row, Escape closes it, each row opens
 *     its own file)
 *   - a failure state when the lookup itself failed
 */
const ProvisionalSourceFileTag = ({ sourceFiles, status, statement, columnLabel, onOpen }: ProvisionalSourceFileTagProps) => {
    const {
        displayState,
        primaryFile,
        moreFiles,
        hasMoreFiles,
        moreFilesLabel,
        moreFilesAccessibleLabel,
        handleMouseEnter,
        handleMouseLeave,
        handleOpenFile,
        triggerRef,
        isFileListOpen,
        fileListPosition,
        fileListRef,
        openFileList,
        scheduleCloseFileList,
        handleFileListKeyDown,
        handleRowKeyDown,
        handleTriggerKeyDown,
        handleTriggerFocus,
    } = useProvisionalSourceFileTagLogic({
        sourceFiles,
        status,
        statement,
        onOpen,
    });

    switch (displayState) {
        case SourceFileTagDisplayState.FAILURE:
            return (
                <span
                    className={clsx(styles.Row, styles.RowMuted)}
                    data-testid={REFERENCE_DOC_TEST_IDS.FAILURE}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    <FetchFailureIcon className={styles.FailureIcon} />
                    <span className={styles.Label}>{SOURCE_UNAVAILABLE_LABEL}</span>
                </span>
            );

        case SourceFileTagDisplayState.EMPTY:
            return (
                <span className={clsx(styles.Row, styles.RowMuted)} data-testid={REFERENCE_DOC_TEST_IDS.NOT_AVAILABLE}>
                    <span className={styles.Label}>{NOT_AVAILABLE_LABEL}</span>
                </span>
            );

        case SourceFileTagDisplayState.FILES:
            if (!primaryFile) return null;
            return (
                <span className={styles.Cell}>
                    <button
                        type="button"
                        className={clsx(styles.Row, styles.Clickable)}
                        data-testid={REFERENCE_DOC_TEST_IDS.PRIMARY_FILE}
                        title={primaryFile.fileName}
                        onClick={() => handleOpenFile(primaryFile)}
                    >
                        <SourceFileIcon className={styles.FileIcon} />
                        <span className={styles.Label}>{primaryFile.fileName}</span>
                    </button>
                    {/* Wrapper is `display: contents` so onKeyDown (Escape) can catch both the trigger
                    and its rows via bubbling, with no effect on layout — see the scss. */}
                    {hasMoreFiles && (
                        <div className={styles.MoreFilesWrapper} onKeyDown={handleFileListKeyDown}>
                            <button
                                type="button"
                                ref={triggerRef}
                                className={clsx(styles.Row, styles.Clickable, styles.MoreFilesTrigger)}
                                data-testid={REFERENCE_DOC_TEST_IDS.MORE_FILES_TRIGGER}
                                aria-label={moreFilesAccessibleLabel}
                                aria-expanded={isFileListOpen}
                                onMouseEnter={openFileList}
                                onMouseLeave={scheduleCloseFileList}
                                onFocus={handleTriggerFocus}
                                onBlur={scheduleCloseFileList}
                                onClick={openFileList}
                                onKeyDown={handleTriggerKeyDown}
                            >
                                {moreFilesLabel}
                            </button>
                            {isFileListOpen && fileListPosition && (
                                <div
                                    ref={fileListRef}
                                    className={styles.Popover}
                                    data-testid={REFERENCE_DOC_TEST_IDS.MORE_FILES_LIST}
                                    style={{ top: fileListPosition.top, left: fileListPosition.left }}
                                    onMouseEnter={openFileList}
                                    onMouseLeave={scheduleCloseFileList}
                                >
                                    {/* Card header: bold title + the column's own period (`columnLabel`,
                                    from AuditedStatementTable's `header.name`). Renders bare with
                                    no `|` when absent — never a dangling separator. */}
                                    <div className={styles.PopoverHeader} data-testid={REFERENCE_DOC_TEST_IDS.MORE_FILES_HEADER}>
                                        <span className={styles.PopoverHeaderTitle}>{REFERENCE_DOCUMENTS_ROW_LABEL}</span>
                                        {columnLabel && <span className={styles.PopoverHeaderMeta}> | {columnLabel}</span>}
                                    </div>
                                    <hr className={styles.PopoverDivider} />
                                    {/* Lists ALL files (primary + extras) — the trigger's count is "how
                                    many ADDITIONAL", so the primary file appears both in the cell
                                    and as this list's first row. Built from primaryFile + moreFiles
                                    (not raw `sourceFiles`) so it inherits the hook's ordering and
                                    stays non-optional. */}
                                    <ul className={styles.TooltipList}>
                                        {[primaryFile, ...moreFiles].map((file) => (
                                            <li key={file.docId}>
                                                <button
                                                    type="button"
                                                    className={styles.TooltipRow}
                                                    data-testid={REFERENCE_DOC_TEST_IDS.MORE_FILES_ROW}
                                                    onClick={() => handleOpenFile(file)}
                                                    onFocus={openFileList}
                                                    onBlur={scheduleCloseFileList}
                                                    onKeyDown={handleRowKeyDown(file)}
                                                >
                                                    <span className={styles.TooltipRowIcon}>
                                                        <SourceFileOpenIcon className={styles.TooltipRowIconGlyph} />
                                                    </span>
                                                    <span className={styles.TooltipRowName}>{file.fileName}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </span>
            );

        // Not a tagged surface — the response carried no status, so there is no cell.
        case SourceFileTagDisplayState.NONE:
        default:
            return null;
    }
};

export default ProvisionalSourceFileTag;
